import type { Metadata } from 'next';
import { translations, type Language } from '@/lib/translations';

/**
 * Browser tab titles, one pattern for the whole platform – most specific first,
 * brand last (tabs cut text off on the right):
 *
 *   Мої сертифікати | CONFY
 *   Трансляція · <назва події> | CONFY
 *   Квитки · <назва> · Конструктор події | CONFY   (builder, set on the client)
 *
 * The server renders the Ukrainian title (the UI language lives in the browser
 * only). DocumentTitleSync re-applies the same title in the chosen language,
 * from PAGE_TITLE_ROUTES below, so both sides read one table.
 */

export type PageTitleKey = keyof typeof translations.uk.pageTitles;

export const BRAND = 'CONFY';
export const TITLE_TEMPLATE = `%s | ${BRAND}`;
/** `<meta name>` carrying the raw event/course/organizer name for the client. */
export const TITLE_NAME_META = 'confy:title-name';

export const withBrand = (title: string) => `${title} | ${BRAND}`;

export function pageTitle(key: PageTitleKey, language: Language = 'uk'): string {
  return translations[language].pageTitles[key];
}

/**
 * Title for a layout that names an event, course or organizer: the page itself
 * reads `<name> | CONFY`, and child pages that set a plain title (e.g.
 * «Трансляція») read `Трансляція · <name> | CONFY`.
 */
export function namedTitleMetadata(name: string): Pick<Metadata, 'title' | 'other'> {
  // Next fills the template with String.replace(/%s/g, …): keep a literal "%s"
  // in a name from being treated as the placeholder.
  const safe = name.replace(/%s/g, '%​s');
  return {
    title: { absolute: withBrand(name), template: `%s · ${safe} | ${BRAND}` },
    other: { [TITLE_NAME_META]: name },
  };
}

export type PageTitleRoute = {
  /** App route with dynamic segments in brackets, route groups left out. */
  route: string;
  key?: PageTitleKey;
  /** Prefixed with the event/course/organizer name from TITLE_NAME_META. */
  named?: boolean;
  /** Title is used as is, without " | CONFY" (it already starts with the brand). */
  bare?: boolean;
  /** The page sets document.title itself; the client sync leaves it alone. */
  clientManaged?: boolean;
};

export const PAGE_TITLE_ROUTES: PageTitleRoute[] = [
  { route: '/', key: 'home', bare: true },
  { route: '/browse', key: 'browse' },
  { route: '/login', key: 'login' },
  { route: '/auth/confirm', key: 'login' },
  { route: '/profile', key: 'profile' },
  { route: '/profile/certificates', key: 'certificates' },
  { route: '/profile/tests', key: 'tests' },
  { route: '/profile/surveys', key: 'surveys' },
  { route: '/profile/tickets', key: 'tickets' },
  { route: '/profile/materials', key: 'materials' },
  { route: '/created', key: 'created' },
  { route: '/dashboard', key: 'dashboard' },
  { route: '/dashboard/crm', key: 'crm' },
  { route: '/dashboard/email-history', key: 'emailHistory' },
  { route: '/dashboard/email-history/[campaignId]', key: 'emailHistory' },
  { route: '/dmail', key: 'dmail' },
  { route: '/organizers', key: 'organizers' },
  { route: '/organizers/[identifier]', named: true },
  { route: '/org/[identifier]', named: true },
  { route: '/certificates/verify', key: 'verifyCertificate' },
  { route: '/certificates/verify/[code]', key: 'verifyCertificate' },
  { route: '/payment/success', key: 'payment' },
  { route: '/privacy', key: 'privacy' },
  { route: '/terms', key: 'terms' },
  { route: '/bpr-apply', key: 'bprApply' },
  { route: '/bpr-applications', key: 'bprApplications' },
  { route: '/bpr-admin', key: 'bprAdmin' },
  { route: '/bpr-admin2', key: 'bprAdmin' },
  { route: '/admin', key: 'admin' },
  { route: '/admin/payments', key: 'adminPayments' },
  { route: '/admin/events', key: 'adminEvents' },
  { route: '/invitations/accept', key: 'invitation' },
  { route: '/invitations/decline', key: 'invitation' },
  { route: '/speaker-invite/[token]', key: 'speakerInvite' },
  { route: '/event/[id]', named: true },
  { route: '/event/[id]/stream', key: 'stream', named: true },
  { route: '/event/[id]/recording/[materialId]', key: 'recording', named: true },
  { route: '/event/[id]/bpr-preview', key: 'bprPreview', named: true },
  { route: '/bevent/[id]', named: true },
  { route: '/courses/[id]', named: true },
  { route: '/courses/[id]/learn', key: 'learn', named: true },
  { route: '/shared/recording/[token]', key: 'recording' },
  { route: '/shared/stats/[token]', key: 'sharedStats', clientManaged: true },
  { route: '/shared/promo/[token]', key: 'sharedPromo' },
];

/** Routes without an entry above, and why. Checked by tests/lib/seo/pageTitles.test.ts. */
export const ROUTES_WITHOUT_TITLE_ENTRY: Record<string, string> = {
  '/builder/[id]': 'builder sets its own per-tab title (src/lib/builder/title.ts)',
  '/builder/new': 'builder sets its own per-tab title (src/lib/builder/title.ts)',
  '/r/[code]': 'promo short link renders the event or course page in place',
};

function matches(route: string, pathname: string): boolean {
  const r = route.split('/').filter(Boolean);
  const p = pathname.split('/').filter(Boolean);
  return r.length === p.length && r.every((seg, i) => seg.startsWith('[') || seg === p[i]);
}

export function findPageTitleRoute(pathname: string): PageTitleRoute | undefined {
  // Exact segments beat dynamic ones (e.g. /certificates/verify vs a [code]).
  const hits = PAGE_TITLE_ROUTES.filter((e) => matches(e.route, pathname));
  return hits.find((e) => !e.route.includes('[')) ?? hits[0];
}

/**
 * The title this pathname should show in `language`, or null to leave
 * document.title alone (unknown route, self-managed page, name not known yet).
 */
export function clientPageTitle(pathname: string, language: Language, name: string | null): string | null {
  const entry = findPageTitleRoute(pathname);
  if (!entry || entry.clientManaged) return null;
  let title: string;
  if (entry.named) {
    if (!name) return null;
    title = entry.key ? `${pageTitle(entry.key, language)} · ${name}` : name;
  } else {
    title = pageTitle(entry.key!, language);
  }
  return entry.bare ? title : withBrand(title);
}

export const normalizeTitle = (s: string) => s.replace(/\s+/g, ' ').trim();
